import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { reducedMotion } from '../motion/motion.styled';

export const CarouselShell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

/**
 * The clipping frame. Cards are absolutely positioned inside it and slide on
 * plain 2D transforms — deliberately no `preserve-3d` / `translateZ`, because
 * Safari does not reliably clip a 3D-transformed subtree against an ancestor's
 * `overflow: hidden`, which is exactly how a carousel ends up widening the page.
 *
 * `touch-action: pan-y` lets a vertical swipe scroll the page as usual while a
 * horizontal swipe is handed to us instead of panning the document sideways.
 */
export const CarouselViewport = styled.div<{ $ratio: string; $maxWidth: string }>`
  position: relative;
  width: min(100%, ${({ $maxWidth }) => $maxWidth});
  aspect-ratio: ${({ $ratio }) => $ratio};
  overflow: hidden;
  border-radius: ${radius.lg};
  background: linear-gradient(150deg, ${color.sandLight}, ${color.cream});
  box-shadow: ${shadow.lift};
  touch-action: pan-y;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

export const CarouselCard = styled.div<{ $offset: number; $active: boolean }>`
  position: absolute;
  inset: 0;
  z-index: ${({ $offset }) => 10 - Math.abs($offset)};
  opacity: ${({ $offset }) => (Math.abs($offset) > 1 ? 0 : 1)};
  transform: translateX(${({ $offset }) => $offset * 54}%)
    scale(${({ $offset }) => 1 - Math.abs($offset) * 0.12});
  filter: ${({ $active }) => ($active ? 'none' : 'blur(2px) saturate(0.85)')};
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  transition:
    transform ${duration.slow} ${ease.out},
    opacity ${duration.slow} ${ease.out},
    filter ${duration.slow} ${ease.out};

  ${reducedMotion};
`;

export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: ${radius.lg};
  user-select: none;
  -webkit-user-drag: none;
`;

export const NavButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 10px;' : 'right: 10px;')}
  transform: translateY(-50%);
  z-index: 20;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${color.forestDeep};
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 20px rgba(43, 42, 39, 0.16);
  transition:
    background ${duration.fast} ${ease.out},
    transform ${duration.fast} ${ease.spring};

  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.08);
  }

  ${reducedMotion};
`;

/** "3 / 7" pill, so the visitor knows how much is left to see. */
export const Counter = styled.span`
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 20;
  padding: 0.3rem 0.7rem;
  border-radius: ${radius.pill};
  background: rgba(43, 42, 39, 0.55);
  backdrop-filter: blur(6px);
  color: #fff;
  font-family: ${font.body};
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  font-variant-numeric: tabular-nums;
`;

export const Dots = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 1.1rem;
`;

export const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '24px' : '8px')};
  height: 8px;
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  background: ${({ $active }) => ($active ? color.forest : 'rgba(74, 112, 97, 0.28)')};
  transition:
    width ${duration.base} ${ease.spring},
    background ${duration.base} ${ease.out};

  ${reducedMotion};
`;
